import { useConfigStore } from "@/store/use-config-store";
import { useEffect, useState } from "react";
import * as Swetrix from "swetrix";
import { useShallow } from "zustand/react/shallow";
import useShouldShowPrompt from "./useShouldShowPrompt";

const webInstallPromptedAt = "webInstallPromptedAt";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

const useWebInstallPrompt = (): [BeforeInstallPromptEvent | null, () => void, () => void] => {
  const [installPromptEvent, setInstallPromptEvent] = useState<BeforeInstallPromptEvent | null>(
    null
  );
  const [userShouldBePromptedToInstall, handleUserSeeingInstallPrompt] =
    useShouldShowPrompt(webInstallPromptedAt);
  const [settings] = useConfigStore(useShallow((state) => [state.settings]));

  useEffect(() => {
    const beforeInstallPromptHandler = (event: Event) => {
      event.preventDefault();

      // check if user has already been asked
      if (userShouldBePromptedToInstall) {
        // store the event for later use
        setInstallPromptEvent(event as BeforeInstallPromptEvent);
      }
    };

    window.addEventListener("beforeinstallprompt", beforeInstallPromptHandler);
    return () => window.removeEventListener("beforeinstallprompt", beforeInstallPromptHandler);
  }, [userShouldBePromptedToInstall]);

  const handleInstallDeclined = () => {
    Swetrix.track({
      ev: "declined_pwa_install",
      unique: true,
      meta: {
        tenant: settings?.shopName
      }
    });
    console.log("declined");
    handleUserSeeingInstallPrompt();
    setInstallPromptEvent(null);
  };

  const handleInstallAccepted = () => {
    if (!installPromptEvent) {
      return;
    }

    Swetrix.track({
      ev: "installed_pwa",
      unique: true,
      meta: {
        tenant: settings?.shopName
      }
    });
    console.log("accepted");
    // show native prompt
    installPromptEvent.prompt();

    // decide what to do after the user chooses
    installPromptEvent.userChoice.then((choice) => {
      // if the user declined, we don't want to show the prompt again
      console.log("accepted");
      if (choice.outcome !== "accepted") {
        handleUserSeeingInstallPrompt();
      }
      setInstallPromptEvent(null);
    });
  };
  return [installPromptEvent, handleInstallDeclined, handleInstallAccepted];
};
export default useWebInstallPrompt;
